import { useParams, Link } from "react-router-dom";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || "");
  const articles = getArticlesByCategory(slug || "");

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 糖果直播`,
    description: category.description,
    url: `https://tangguolive.com/category/${category.slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "糖果直播",
      url: "https://tangguolive.com",
    },
  };

  return (
    <>
      <title>{`${category.name} - 糖果直播 | 直播资讯与内容平台`}</title>
      <meta name="description" content={`糖果直播${category.name}分类，${category.description}。浏览最新${category.name}相关内容。`} />
      <meta name="keywords" content={`糖果直播,${category.name},直播,在线观看`} />
      <link rel="canonical" href={`https://tangguolive.com/category/${category.slug}`} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={`${category.name} - 糖果直播`} />
      <meta property="og:description" content={category.description} />
      <meta property="og:url" content={`https://tangguolive.com/category/${category.slug}`} />

      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>

      <div className="pt-20 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category.name },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              {category.name}
            </h1>
            <p className="text-sm text-foreground-600">{category.description}</p>
          </div>

          <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl bg-background-100 overflow-hidden hover:bg-background-200/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`糖果直播-${article.title}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2.5 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3 whitespace-nowrap">
                      {category.name}
                    </span>
                    <h2 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                      {article.title}
                    </h2>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}